import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { FollowButton } from "@/components/social/FollowButton";
import { PostCard } from "@/components/social/PostCard";
import { Trophy, Award, MessageCircle, Bookmark, Send, Users, Radio, Gauge, MapPin } from "lucide-react";

function Avatar({ url, name, size = 80 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        return <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center" style={{ width: size, height: size, fontSize: size * 0.4 }}>
            {initial}
        </div>
    );
}

export default function UserProfile() {
    const { userId } = useParams();
    const { user: me } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [collections, setCollections] = useState([]);
    const [followerCount, setFollowerCount] = useState(0);
    const [listMode, setListMode] = useState(null); // null | "followers" | "following"
    const [listItems, setListItems] = useState([]);
    const [listLoading, setListLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setListMode(null);
        (async () => {
            try {
                const r = await api.get(`/social/users/${userId}`);
                if (!cancelled) {
                    setData(r.data);
                    setFollowerCount(r.data?.follower_count || 0);
                }
                try {
                    const cr = await api.get(`/users/${userId}/collections`);
                    if (!cancelled) setCollections(cr.data?.items || []);
                } catch (e) { /* ignore */ }
            } catch (e) {
                if (!cancelled) setError(e?.response?.status === 404 ? "User not found" : "Failed to load profile");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [userId]);

    async function openList(mode) {
        if (listMode === mode) { setListMode(null); return; }
        setListMode(mode);
        setListLoading(true);
        try {
            const r = await api.get(`/social/users/${userId}/${mode}`);
            setListItems(r.data?.items || []);
        } catch { setListItems([]); }
        finally { setListLoading(false); }
    }

    if (loading) return <div className="ok-container py-12 font-mono text-xs opacity-50">Loading dossier…</div>;
    if (error) return <div className="ok-container py-12">{error}</div>;
    const u = data.user;
    const cs = data.recent_comments || [];
    const posts = data.recent_posts || [];
    const canMessage = me && me.id !== u.id;

    return (
        <div className="ok-container py-10 max-w-4xl" data-testid="user-profile-page">
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">Witness Profile · UNVEILED</div>

            <div className="ok-card p-8 flex flex-col md:flex-row items-start gap-6">
                <Avatar url={u.avatar_url} name={u.name} size={96} />
                <div className="flex-1">
                    <h1 className="font-display text-3xl flex items-center gap-3 flex-wrap" data-testid="profile-name">
                        {u.name || "Witness"}
                        {u.subscription_tier === "research_council" && (
                            <span className="inline-flex items-center gap-1.5 ok-card px-2 py-0.5" style={{ borderColor: "#e7c873" }} data-testid="profile-badge-council">
                                <MembershipBadge tier="research_council" size="md" />
                                <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "#e7c873" }}>Research Council · Trusted Source</span>
                            </span>
                        )}
                        {u.subscription_tier === "verified_witness" && (
                            <span className="inline-flex items-center gap-1.5 ok-card px-2 py-0.5" data-testid="profile-badge-witness">
                                <MembershipBadge tier="verified_witness" size="md" />
                                <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">Verified Witness</span>
                            </span>
                        )}
                    </h1>
                    {u.location ? (
                        <div className="mt-1.5 text-xs font-mono opacity-60 flex items-center gap-1.5" data-testid="profile-location-display">
                            <MapPin className="w-3 h-3" /> {u.location}
                        </div>
                    ) : null}
                    {u.bio ? <p className="mt-3 text-sm opacity-80 max-w-2xl whitespace-pre-wrap break-words leading-relaxed" data-testid="profile-bio-display">{u.bio}</p> : null}

                    {/* Social graph counts */}
                    <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="profile-social-counts">
                        <button onClick={() => openList("followers")} className={`ok-tag inline-flex items-center gap-1 hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)] transition ${listMode === "followers" ? "border-[var(--ok-accent)] text-[var(--ok-accent)]" : ""}`} data-testid="profile-followers-btn">
                            <Users className="w-3 h-3" />
                            <strong className="text-[var(--ok-accent)]">{followerCount}</strong>
                            <span>Followers</span>
                        </button>
                        <button onClick={() => openList("following")} className={`ok-tag inline-flex items-center gap-1 hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)] transition ${listMode === "following" ? "border-[var(--ok-accent)] text-[var(--ok-accent)]" : ""}`} data-testid="profile-following-btn">
                            <Users className="w-3 h-3" />
                            <strong className="text-[var(--ok-accent)]">{data.following_count || 0}</strong>
                            <span>Following</span>
                        </button>
                        <div className="ok-tag inline-flex items-center gap-1" data-testid="profile-reputation">
                            <Trophy className="w-3 h-3" />
                            <span>Reputation</span>
                            <strong className="text-[var(--ok-accent)]">{u.reputation}</strong>
                        </div>
                        <div className="ok-tag inline-flex items-center gap-1" data-testid="profile-contribution-score">
                            <Gauge className="w-3 h-3" />
                            <span>Contribution</span>
                            <strong className="text-[var(--ok-accent)]">{data.contribution_score || 0}</strong>
                        </div>
                        {u.subscription_tier && u.subscription_tier !== "free" && (
                            <div className="ok-tag inline-flex items-center gap-1" style={{ borderColor: "var(--ok-accent)", color: "var(--ok-accent)" }}>
                                <Award className="w-3 h-3" />
                                {u.subscription_tier === "research_council" ? "Research Council" : "Verified Witness"}
                                {u.founding_member ? " · Founding" : ""}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                        <FollowButton
                            userId={u.id}
                            initialFollowing={!!data.is_following}
                            onChange={(_, count) => setFollowerCount(count)}
                        />
                        {canMessage && (
                            <Link to={`/messages/${u.id}`} className="ok-btn-outline text-xs inline-flex items-center gap-1" data-testid="profile-message-btn">
                                <Send className="w-3 h-3" /> Message
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            {/* Followers / Following list */}
            {listMode && (
                <div className="ok-card p-4 mt-4" data-testid={`profile-${listMode}-list`}>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-3">{listMode === "followers" ? "Followers" : "Following"}</div>
                    {listLoading ? (
                        <div className="text-xs font-mono opacity-50">Loading…</div>
                    ) : listItems.length === 0 ? (
                        <div className="text-sm opacity-60">{listMode === "followers" ? "No followers yet." : "Not following anyone yet."}</div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-2">
                            {listItems.map((p) => (
                                <Link key={p.id} to={`/u/${p.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--ok-surface-2)] transition" data-testid={`profile-list-user-${p.id}`}>
                                    <Avatar url={p.avatar_url} name={p.name} size={28} />
                                    <span className="text-sm truncate flex-1">{p.name}</span>
                                    {p.subscription_tier === "research_council" && <MembershipBadge tier="research_council" size="sm" />}
                                    {p.subscription_tier === "verified_witness" && <MembershipBadge tier="verified_witness" size="sm" />}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Recent posts (Live Drops) */}
            {posts.length > 0 && (
                <>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-10 mb-3 flex items-center gap-2">
                        <Radio className="w-3 h-3" />
                        Recent drops ({posts.length})
                    </div>
                    <div className="space-y-3" data-testid="profile-posts">
                        {posts.map((p) => <PostCard key={p.id} post={p} compact />)}
                    </div>
                </>
            )}

            {collections.length > 0 && (
                <>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-10 mb-3 flex items-center gap-2">
                        <Bookmark className="w-3 h-3" />
                        Public collections ({collections.length})
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="profile-collections">
                        {collections.map((c) => (
                            <Link key={c.id} to={`/collections/${c.id}`} className="ok-card p-4 block hover:border-[var(--ok-accent)] transition" data-testid={`profile-collection-${c.id}`}>
                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">{c.item_count} items</div>
                                <div className="font-display text-lg mt-1">{c.name}</div>
                                {c.description && <p className="text-xs opacity-70 mt-1 line-clamp-2">{c.description}</p>}
                            </Link>
                        ))}
                    </div>
                </>
            )}

            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-10 mb-3 flex items-center gap-2">
                <MessageCircle className="w-3 h-3" />
                Recent contributions ({cs.length})
            </div>

            {cs.length === 0 ? (
                <div className="ok-card p-6 text-center text-sm opacity-70">No comments yet.</div>
            ) : (
                <div className="space-y-3">
                    {cs.map((c) => (
                        <Link
                            key={c.id}
                            to={c.target_type === "encounter" ? `/encounters/${c.target_id}` : c.target_type === "media" ? `/media-archive/${c.target_id}` : c.target_type === "post" ? "/community" : `/species/${c.target_id}`}
                            className="ok-card p-4 block hover:border-[var(--ok-accent)] transition"
                            data-testid={`profile-comment-${c.id}`}
                        >
                            <div className="text-xs font-mono opacity-60 mb-1.5 flex items-center gap-2">
                                <span className="ok-tag">{c.target_type}</span>
                                <span>{new Date(c.created_at).toLocaleString()}</span>
                                <span className="ml-auto">↑ {c.score || 0}</span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap line-clamp-3">{c.body}</p>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
